import { type ChangeEvent, useEffect, useState } from 'react';
import { ArrowLeft, CheckCircle, FileText, Search, SquarePen } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';

import { InstructorLoadingSkeleton } from '../components/layout/InstructorLoadingSkeleton';
import {
  instructorService,
  type CourseSubmission,
  type InstructorCourse,
} from '../services/instructorService';
import { formatEnglishDate } from '../lib/dateFormat';

type SubmissionStatusFilter = CourseSubmission['status'] | 'all';

const statusFilters: SubmissionStatusFilter[] = [
  'all',
  'submitted',
  'scoring',
  'scored',
  'reviewed',
];

const getStatusClass = (status: CourseSubmission['status']) => {
  const statusClasses: Record<CourseSubmission['status'], string> = {
    submitted: 'instructor-status-submitted',
    scoring: 'instructor-status-scoring',
    scored: 'instructor-status-scored',
    reviewed: 'instructor-status-reviewed',
  };

  return statusClasses[status];
};

const isStatusFilter = (value: string): value is SubmissionStatusFilter =>
  statusFilters.some((status) => status === value);

const formatStatus = (status: string) =>
  status.charAt(0).toUpperCase() + status.slice(1);

const formatDate = (dateString: string) =>
  formatEnglishDate(dateString, {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });

const getScore = (submission: CourseSubmission) => {
  const score = Number(submission.avg_score);

  if (!Number.isFinite(score)) {
    return 0;
  }

  return score;
};

const getScorePercent = (submission: CourseSubmission) =>
  Math.min(100, Math.max(0, (getScore(submission) / 3) * 100));

const CourseSubmissions = () => {
  const { courseId } = useParams<{ courseId: string }>();
  const navigate = useNavigate();
  const [course, setCourse] = useState<InstructorCourse | null>(null);
  const [submissions, setSubmissions] = useState<CourseSubmission[]>([]);
  const [filteredSubmissions, setFilteredSubmissions] = useState<CourseSubmission[]>([]);
  const [selectedSubmissionId, setSelectedSubmissionId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState<SubmissionStatusFilter>('all');

  useEffect(() => {
    const fetchSubmissions = async () => {
      if (!courseId) {
        setError('Course not found');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);
        const [coursesData, submissionsData] = await Promise.all([
          instructorService.getCourses(),
          instructorService.getCourseSubmissions(courseId),
        ]);
        const currentCourse =
          coursesData.find((courseItem) => courseItem.id === courseId) ?? null;
        const sortedSubmissions = [...submissionsData].sort(
          (a, b) =>
            new Date(b.submitted_at).getTime() - new Date(a.submitted_at).getTime()
        );

        setCourse(currentCourse);
        setSubmissions(sortedSubmissions);
        setFilteredSubmissions(sortedSubmissions);
        setSelectedSubmissionId((current) =>
          sortedSubmissions.some((submission) => submission.id === current)
            ? current
            : sortedSubmissions[0]?.id ?? null
        );
      } catch (err) {
        console.error('Error fetching submissions:', err);
        setError('Failed to load submissions');
      } finally {
        setLoading(false);
      }
    };

    fetchSubmissions();
  }, [courseId]);

  useEffect(() => {
    const normalizedSearch = searchQuery.trim().toLowerCase();
    const nextFiltered = submissions.filter((submission) => {
      const matchesSearch =
        !normalizedSearch ||
        submission.student_name.toLowerCase().includes(normalizedSearch) ||
        submission.student_email.toLowerCase().includes(normalizedSearch) ||
        submission.project_title.toLowerCase().includes(normalizedSearch) ||
        submission.name.toLowerCase().includes(normalizedSearch) ||
        submission.status.toLowerCase().includes(normalizedSearch);
      const matchesStatus =
        filterStatus === 'all' || submission.status === filterStatus;

      return matchesSearch && matchesStatus;
    });

    setFilteredSubmissions(nextFiltered);
    setSelectedSubmissionId((current) =>
      nextFiltered.some((submission) => submission.id === current)
        ? current
        : nextFiltered[0]?.id ?? null
    );
  }, [filterStatus, searchQuery, submissions]);

  const handleStatusChange = (event: ChangeEvent<HTMLSelectElement>) => {
    const nextStatus = event.target.value;

    if (isStatusFilter(nextStatus)) {
      setFilterStatus(nextStatus);
    }
  };

  const selectedSubmission =
    filteredSubmissions.find((submission) => submission.id === selectedSubmissionId) ??
    filteredSubmissions[0] ??
    null;
  const scoredCount = submissions.filter(
    (submission) => submission.status === 'scored' || submission.status === 'reviewed'
  ).length;
  const reviewedCount = submissions.filter(
    (submission) => submission.status === 'reviewed'
  ).length;

  if (loading) {
    return (
      <div className="instructor-page">
        <InstructorLoadingSkeleton
          message="Loading course submissions..."
          variant="submissions"
        />
      </div>
    );
  }

  return (
    <div className="instructor-page">
      <div className="instructor-top-bar">
        <div>
          <button
            className="instructor-secondary-button mb-3"
            onClick={() => navigate('/instructor/courses')}
            type="button"
          >
            <ArrowLeft className="h-4 w-4" strokeWidth={2.4} />
            Back to Courses
          </button>
          <h1 className="instructor-page-title">Course Submissions</h1>
          <div className="instructor-page-sub">
            {course ? `${course.code} - ${course.title}` : 'Course submissions'}
          </div>
        </div>
      </div>

      {error && <div className="instructor-error-state">{error}</div>}

      <div className="instructor-stat-row">
        <div className="instructor-stat-card navy">
          <div className="instructor-stat-label">Total Submissions</div>
          <div className="instructor-stat-value">{submissions.length}</div>
          <div className="instructor-stat-sub">Received in this course</div>
        </div>
        <div className="instructor-stat-card">
          <div className="instructor-stat-label">Scored</div>
          <div className="instructor-stat-value">{scoredCount}</div>
          <div className="instructor-stat-sub">With generated or reviewed scores</div>
        </div>
        <div className="instructor-stat-card">
          <div className="instructor-stat-label">Reviewed</div>
          <div className="instructor-stat-value">{reviewedCount}</div>
          <div className="instructor-stat-sub">Finalized by instructor</div>
        </div>
        <div className="instructor-stat-card">
          <div className="instructor-stat-label">Visible</div>
          <div className="instructor-stat-value small">{filteredSubmissions.length}</div>
          <div className="instructor-stat-sub">After filters</div>
        </div>
      </div>

      <div className="instructor-split-grid">
        <aside className="instructor-list-panel">
          <div className="instructor-list-header">
            <div className="instructor-list-title-row">
              <div className="instructor-list-title">
                <span>Submissions</span>
                <span className="instructor-list-count">{filteredSubmissions.length}</span>
              </div>
            </div>
            <div className="instructor-search-wrap">
              <Search strokeWidth={2} />
              <input
                onChange={(event) => setSearchQuery(event.target.value)}
                placeholder="Search by student, project, status..."
                type="search"
                value={searchQuery}
              />
            </div>
            <div className="instructor-form-control">
              <select
                className="instructor-select"
                onChange={handleStatusChange}
                value={filterStatus}
              >
                {statusFilters.map((status) => (
                  <option key={status} value={status}>
                    {status === 'all' ? 'All statuses' : formatStatus(status)}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="instructor-list-scroll">
            {filteredSubmissions.length === 0 ? (
              <div className="instructor-empty-state">No submissions found.</div>
            ) : (
              filteredSubmissions.map((submission) => (
                <button
                  className={
                    submission.id === selectedSubmission?.id
                      ? 'instructor-list-item active text-left'
                      : 'instructor-list-item text-left'
                  }
                  key={submission.id}
                  onClick={() => setSelectedSubmissionId(submission.id)}
                  type="button"
                >
                  <div className="instructor-list-item-top">
                    <span className="instructor-list-item-title">
                      {submission.student_name}
                    </span>
                    <span className={`instructor-status-badge ${getStatusClass(submission.status)}`}>
                      {formatStatus(submission.status)}
                    </span>
                  </div>
                  <div className="instructor-list-item-sub">{submission.project_title}</div>
                  <div className="instructor-list-item-meta">
                    <span>{submission.name}</span>
                    <span>{formatDate(submission.submitted_at)}</span>
                  </div>
                  <div className="mt-3 flex items-center justify-between gap-3">
                    <div className="instructor-score-bar" aria-hidden="true">
                      <div
                        className="instructor-score-fill"
                        style={{ width: `${getScorePercent(submission)}%` }}
                      />
                    </div>
                    <span className="text-xs font-bold text-[var(--instructor-navy)]">
                      {getScore(submission).toFixed(1)}/3
                    </span>
                  </div>
                </button>
              ))
            )}
          </div>
        </aside>

        <section className="instructor-detail-panel">
          {selectedSubmission ? (
            <>
              <div className="instructor-detail-header">
                <div className="min-w-0">
                  <div className="mb-2 flex flex-wrap items-center gap-2">
                    {course && <span className="instructor-badge">{course.code}</span>}
                    <span className={`instructor-status-badge ${getStatusClass(selectedSubmission.status)}`}>
                      {formatStatus(selectedSubmission.status)}
                    </span>
                  </div>
                  <h2 className="instructor-detail-title">
                    {selectedSubmission.project_title}
                  </h2>
                  <div className="instructor-detail-sub">
                    {selectedSubmission.name} by {selectedSubmission.student_name}
                  </div>
                </div>
                <button
                  className="instructor-button"
                  onClick={() => navigate(`/instructor/submissions/${selectedSubmission.id}`)}
                  type="button"
                >
                  <SquarePen className="h-4 w-4" strokeWidth={2.4} />
                  Open Review
                </button>
              </div>

              <div className="instructor-detail-body">
                <div className="instructor-meta-grid">
                  <div className="instructor-meta-card">
                    <div className="instructor-meta-label">Student</div>
                    <div className="instructor-meta-value">
                      {selectedSubmission.student_name}
                    </div>
                    <div className="instructor-card-sub">{selectedSubmission.student_email}</div>
                  </div>
                  <div className="instructor-meta-card">
                    <div className="instructor-meta-label">Submitted</div>
                    <div className="instructor-meta-value">
                      {formatDate(selectedSubmission.submitted_at)}
                    </div>
                  </div>
                  <div className="instructor-meta-card">
                    <div className="instructor-meta-label">Average Score</div>
                    <div className="instructor-meta-value">
                      {getScore(selectedSubmission).toFixed(1)}/3
                    </div>
                    <div className="instructor-card-sub">
                      {selectedSubmission.scores_count} scored dimensions
                    </div>
                  </div>
                </div>

                <div className="instructor-feedback-list">
                  <div className="instructor-feedback-item">
                    <FileText strokeWidth={2.2} />
                    <div>
                      <div className="instructor-note-title">Submission File</div>
                      <div className="instructor-note-text">
                        {selectedSubmission.file_type || 'Submission file'}
                      </div>
                    </div>
                  </div>
                  <div className="instructor-feedback-item">
                    <CheckCircle strokeWidth={2.2} />
                    <div>
                      <div className="instructor-note-title">Instructor Feedback</div>
                      <div className="instructor-note-text">
                        {selectedSubmission.has_instructor_feedback
                          ? 'Feedback has been added for this submission.'
                          : 'No instructor feedback has been added yet.'}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </>
          ) : (
            <div className="instructor-empty-state">Select a submission to review.</div>
          )}
        </section>
      </div>
    </div>
  );
};

export default CourseSubmissions;
